import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSocialProjects, useDeleteSocialProject, useReorderSocialProjects } from "./queries";
import { showDeleteConfirm } from "../../components/Common/DeleteConfirm";
import { QueryError } from "../../components/Common/QueryError";
import { errorMessage } from "@/lib/error-message";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface SocialProjectRow {
  id: string;
  title: string;
  slug: string;
  slugEn?: string | null;
  order: number;
  images: string[] | null;
  published: boolean;
}

function SortableRow({
  project,
  onEdit,
  onDelete,
}: {
  project: SocialProjectRow;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: project.id,
  });

  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(isDragging && "relative z-10 bg-muted/60 shadow-md")}
    >
      <TableCell className="text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1 rounded text-muted-foreground/60 hover:text-foreground cursor-grab active:cursor-grabbing"
            title="Sürükleyerek sırala"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          {project.order}
        </div>
      </TableCell>
      <TableCell>
        {project.images?.[0] ? (
          <img
            src={project.images[0]}
            alt={project.title}
            className="h-10 w-10 object-cover rounded"
          />
        ) : (
          <div className="h-10 w-10 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">
            ?
          </div>
        )}
      </TableCell>
      <TableCell className="font-medium">{project.title}</TableCell>
      <TableCell className="text-muted-foreground text-sm">
        <div>{project.slug}</div>
        {project.slugEn && project.slugEn !== project.slug && (
          <div className="truncate text-xs text-muted-foreground/70">EN: {project.slugEn}</div>
        )}
      </TableCell>
      <TableCell>
        {project.published ? (
          <Badge variant="default">Yayında</Badge>
        ) : (
          <Badge variant="outline">Taslak</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function SocialList() {
  const navigate = useNavigate();
  const { data: rawProjects, isLoading, isError, error, refetch } = useSocialProjects();
  const deleteMutation = useDeleteSocialProject();
  const reorderMutation = useReorderSocialProjects();

  // Sürükleme sırasında anında geri bildirim için yerel kopya
  const [projects, setProjects] = useState<SocialProjectRow[]>([]);
  useEffect(() => {
    setProjects(Array.isArray(rawProjects) ? (rawProjects as SocialProjectRow[]) : []);
  }, [rawProjects]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = projects.findIndex((p) => p.id === active.id);
    const newIndex = projects.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const next = arrayMove(projects, oldIndex, newIndex).map((p, i) => ({
      ...p,
      order: i,
    }));
    setProjects(next);

    reorderMutation.mutate(
      next.map((p) => ({ id: p.id, order: p.order })),
      {
        onSuccess: () => toast.success("Sıralama kaydedildi"),
        onError: (e: unknown) => {
          toast.error(errorMessage(e, "Sıralama kaydedilemedi"));
          setProjects(Array.isArray(rawProjects) ? (rawProjects as SocialProjectRow[]) : []);
        },
      },
    );
  }

  function handleDelete(id: string) {
    showDeleteConfirm({
      onConfirm: () => {
        deleteMutation.mutate(id, {
          onSuccess: () => toast.success("Proje silindi"),
          onError: (e: unknown) => toast.error(errorMessage(e, "Silinemedi")),
        });
      },
    });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Sosyal Sorumluluk Projeleri</h1>
        <Button onClick={() => navigate("/social/new")}>
          <Plus className="h-4 w-4 mr-2" />
          Yeni Ekle
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError && !rawProjects ? (
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : projects.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
          Henüz sosyal sorumluluk projesi eklenmedi.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Sıra</TableHead>
                <TableHead className="w-16">Görsel</TableHead>
                <TableHead>Başlık</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead className="w-28">Durum</TableHead>
                <TableHead className="w-28 text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <SortableContext
                items={projects.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                {projects.map((project) => (
                  <SortableRow
                    key={project.id}
                    project={project}
                    onEdit={() => navigate(`/social/${project.id}/edit`)}
                    onDelete={() => handleDelete(project.id)}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </DndContext>
      )}
    </div>
  );
}
